import { useEffect, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { Brain, ArrowsClockwise, CaretDown, CaretUp, Sparkle } from "@phosphor-icons/react";

const PRIORITY_META = {
  P1: { label: "Priority 1 — research services company", cls: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  P2: { label: "Priority 2 — pharma / biotech / device", cls: "bg-sky-100 text-sky-800 border-sky-200" },
  respondent: { label: "Respondent-side — panel, not a client", cls: "bg-amber-100 text-amber-800 border-amber-200" },
  competitor: { label: "Competitor — fieldwork agency", cls: "bg-rose-100 text-rose-800 border-rose-200" },
  not_relevant: { label: "Not a likely client", cls: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

export const FitScanPanel = ({ domain, onApplyKeywords, onScan, myKeywords = [], trigger = 0 }) => {
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(false);
  const [kwSel, setKwSel] = useState(new Set());
  const [mySel, setMySel] = useState(new Set());
  const [open, setOpen] = useState(true);

  const d = (domain || "").trim().toLowerCase();
  const validDomain = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}$/i.test(d);

  useEffect(() => {
    setScan(null);
    setMySel(new Set());
    if (!validDomain) return;
    api.fitscanGet(d).then(s => {
      if (!s.missing) { setScan(s); onScan?.(s); setKwSel(new Set((s.suggested_keywords || []).map(k => k.keyword))); setOpen(false); }
    }).catch(() => {});
  }, [d]); // eslint-disable-line

  useEffect(() => {
    if (!trigger || loading) return;
    if (!validDomain) { toast.error("Enter a company domain first (e.g. lilly.com)"); return; }
    if (scan) setOpen(true); else run();
  }, [trigger]); // eslint-disable-line

  const run = async () => {
    setLoading(true);
    try {
      const s = await api.fitscanAnalyze({ domain: d });
      setScan(s);
      onScan?.(s);
      setKwSel(new Set((s.suggested_keywords || []).map(k => k.keyword)));
      setOpen(true);
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setLoading(false); }
  };

  if (!validDomain) return null;
  const meta = scan ? (PRIORITY_META[scan.priority] || PRIORITY_META.not_relevant) : null;

  return (
    <div className="rounded-lg border border-indigo-200 bg-white overflow-hidden" data-testid="fitscan-panel">
      <div className="px-3 py-2.5 flex items-center gap-2 flex-wrap">
        <Brain size={16} weight="fill" className="text-indigo-600" />
        <span className="text-sm font-medium text-zinc-900">2 · Fit Scan</span>
        {!scan && <span className="text-xs text-zinc-400">AI judges {d} against the Medicys ICP, reads its live job ads, and writes tailored search keywords (~2–3 Apollo credits + AI)</span>}
        {scan && meta && <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${meta.cls}`} data-testid="fitscan-priority">{meta.label}</span>}
        {scan && <span className="text-xs text-zinc-400">{scan.org_type}{scan.org_employees ? ` · ~${Number(scan.org_employees).toLocaleString()} employees` : ""} · {scan.census_count} titles scanned · {(scan.job_ads || []).length} job ads read</span>}
        <div className="ml-auto flex items-center gap-1.5">
          {scan && (
            <button type="button" onClick={() => setOpen(o => !o)} className="p-1 text-zinc-400 hover:text-zinc-600" data-testid="fitscan-toggle">
              {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
            </button>
          )}
          <button type="button" onClick={run} disabled={loading} data-testid="fitscan-btn"
            className="h-8 px-3 rounded-md bg-indigo-600 text-white text-xs font-medium hover:bg-indigo-700 disabled:opacity-40 flex items-center gap-1.5">
            {loading ? "Analysing…" : scan ? <><ArrowsClockwise size={13} /> Re-scan</> : <><Sparkle size={13} weight="fill" /> Fit scan (AI)</>}
          </button>
        </div>
      </div>
      {scan && open && (
        <div className="border-t border-indigo-100 px-3 py-3 space-y-3">
          <p className="text-sm text-zinc-700" data-testid="fitscan-verdict">{scan.verdict}</p>
          {scan.diversified && (
            <p className="text-xs text-amber-700" data-testid="fitscan-diversified">
              ⚠ Diversified company{(scan.divisions || []).length ? ` — divisions: ${scan.divisions.join(", ")}` : ""}. Healthcare is only part of it: keywords are restricted to healthcare-specific terms, division-ambiguous titles get rated C at most, and the Apollo panel defaults to "healthcare profiles only".
            </p>
          )}
          {(scan.outsourcing_signals || []).length > 0 && (
            <div className="text-xs text-zinc-600" data-testid="fitscan-signals">
              <span className="font-medium text-zinc-900">Outsourcing signals:</span>
              <ul className="list-disc ml-4 mt-1 space-y-0.5">{scan.outsourcing_signals.map((s, i) => <li key={i}>{s}</li>)}</ul>
            </div>
          )}
          {(scan.approach_first || []).length > 0 && (
            <div className="text-xs text-zinc-600" data-testid="fitscan-approach"><span className="font-medium text-zinc-900">Approach first:</span> {scan.approach_first.join(" · ")}</div>
          )}
          <div>
            {(scan.suggested_keywords || []).length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2 mb-1.5">
                  <span className="text-[11px] font-medium text-indigo-600 uppercase tracking-wider">AI keywords for this company — untick any you don't want</span>
                  <button type="button" data-testid="fitscan-kw-all" onClick={() => setKwSel(new Set((scan.suggested_keywords || []).map(k => k.keyword)))}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-indigo-200 text-indigo-600 hover:border-indigo-400">Select all</button>
                  <button type="button" data-testid="fitscan-kw-none" onClick={() => setKwSel(new Set())}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-zinc-200 text-zinc-500 hover:border-zinc-400">None</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {scan.suggested_keywords.map((k, i) => {
                    const on = kwSel.has(k.keyword);
                    return (
                      <button key={i} type="button" title={k.why} data-testid={`fitscan-kw-${i}`}
                        onClick={() => setKwSel(prev => { const n = new Set(prev); n.has(k.keyword) ? n.delete(k.keyword) : n.add(k.keyword); return n; })}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400"}`}>
                        {k.keyword}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {myKeywords.length > 0 && (
              <>
                <div className="flex flex-wrap items-center gap-2 mt-3 mb-1.5">
                  <span className="text-[11px] font-medium text-zinc-500 uppercase tracking-wider">My keywords — tick any to add to the search</span>
                  <button type="button" data-testid="fitscan-mykw-all" onClick={() => setMySel(new Set(myKeywords))}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-zinc-300 text-zinc-600 hover:border-zinc-500">Select all</button>
                  <button type="button" data-testid="fitscan-mykw-none" onClick={() => setMySel(new Set())}
                    className="text-[11px] px-1.5 py-0.5 rounded border border-zinc-200 text-zinc-500 hover:border-zinc-400">None</button>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {myKeywords.map((kw, i) => {
                    const on = mySel.has(kw);
                    return (
                      <button key={kw} type="button" data-testid={`fitscan-mykw-${i}`}
                        onClick={() => setMySel(prev => { const n = new Set(prev); n.has(kw) ? n.delete(kw) : n.add(kw); return n; })}
                        className={`text-xs px-2 py-1 rounded-full border transition-colors ${on ? "bg-zinc-950 text-white border-zinc-950" : "bg-white text-zinc-500 border-zinc-200 hover:border-zinc-400"}`}>
                        {kw}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
            {(() => {
              const merged = [...new Set([
                ...(scan.suggested_keywords || []).filter(k => kwSel.has(k.keyword)).map(k => k.keyword),
                ...myKeywords.filter(k => mySel.has(k)),
              ])];
              return (
                <button type="button" disabled={merged.length === 0} data-testid="fitscan-run-btn"
                  onClick={() => onApplyKeywords(merged)}
                  className="mt-3 h-9 px-4 rounded-md bg-zinc-950 text-white text-sm font-medium hover:bg-zinc-800 disabled:opacity-40">
                  Search Hunter with {merged.length} keyword{merged.length === 1 ? "" : "s"} → then Apollo below
                </button>
              );
            })()}
          </div>
          {["respondent", "competitor", "not_relevant"].includes(scan.priority) && (
            <p className="text-xs text-amber-600" data-testid="fitscan-notarget">The ICP says don't prospect here — you can still search manually if you disagree.</p>
          )}
        </div>
      )}
    </div>
  );
};
